import { BaseService } from './base.service';
import { User } from '../models/user.models';
import type {
  CreateEquipmentRaceLogPayload,
  CreateTeamRaceLogsPayload,
  GetStartEndDatesParams,
  UpdateRaceLogPayload,
  LinkEquipmentPayload,
} from '../models/race.models';

const getAuthHeaders = () => {
  const user: User = JSON.parse(window.localStorage.getItem('user') as string);
  return {
    'x-access-token': user.token,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
};

export default class RaceLogsService extends BaseService {
  /** GET /v2/equipment/<equipment_id>/race-logs - get all race logs for the user's equipment */
  public async getEquipmentRaceLogs(equipmentId: number, params: GetStartEndDatesParams) {
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    return this.axios.get(`${this.backendUrl}/v2/equipment/${equipmentId}/race-logs?${searchParams.toString()}`, {
      headers: getAuthHeaders(),
    });
  }

  public async getTeamRaceLogs(teamId: string, trainingId: string, params: GetStartEndDatesParams) {
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    return this.axios.get(
      `${this.backendUrl}/team/${teamId}/training/${trainingId}/race-logs?${searchParams.toString()}`,
      {
        headers: getAuthHeaders(),
      }
    );
  }

  public async getAthleteRaceLogs(params: GetStartEndDatesParams) {
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    return this.axios.get(`${this.backendUrl}/athlete/race-logs?${searchParams.toString()}`, {
      headers: {
        ...getAuthHeaders(),
      },
    });
  }

  /** POST /v2/equipment/<equipment_id>/race-logs - create a new equipment race log */
  public async createEquipmentRaceLog(equipmentId: number, payload: CreateEquipmentRaceLogPayload) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.post(`${this.backendUrl}/v2/equipment/${equipmentId}/race-logs`, payload, {
      headers: {
        ...getAuthHeaders(),
        'Measurement-System': user.measurementSystem ?? 'imperial',
      },
    });
  }

  public async createTeamRaceLogs(teamId: string, trainingId: string, payload: CreateTeamRaceLogsPayload) {
    return this.axios.post(`${this.backendUrl}/team/${teamId}/training/${trainingId}/race-logs`, payload, {
      headers: getAuthHeaders(),
    });
  }

  /** GET /race-log/<id> - get a single race log */
  public async getRaceLog(id: number) {
    return this.axios.get(`${this.backendUrl}/race-log/${id}`, {
      headers: getAuthHeaders(),
    });
  }

  /** PUT /race-log/<id> - update a race log */
  public async updateRaceLog(id: number, payload: UpdateRaceLogPayload) {
    return this.axios.put(`${this.backendUrl}/race-log/${id}`, payload, {
      headers: getAuthHeaders(),
    });
  }

  public async linkEquipment(id: number, payload: LinkEquipmentPayload) {
    return this.axios.post(`${this.backendUrl}/race-log/${id}`, payload, {
      headers: getAuthHeaders(),
    });
  }

  /** DELETE /race-log/<id> - delete a race log */
  public async deleteRaceLog(id: number) {
    return this.axios.delete(`${this.backendUrl}/race-log/${id}`, {
      headers: getAuthHeaders(),
    });
  }

  /** GET /athlete/races - get races for an athlete for a given start/end date */
  public async getAthleteRaces(params: GetStartEndDatesParams) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    return this.axios.get(`${this.backendUrl}/athlete/races?${searchParams.toString()}`, {
      headers: {
        ...getAuthHeaders(),
        'Measurement-System': user.measurementSystem ?? 'imperial',
      },
    });
  }

  public async getTeamRaces(teamId: string, params: GetStartEndDatesParams) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const searchParams = new URLSearchParams();
    searchParams.set('start_date', params.start_date);
    searchParams.set('end_date', params.end_date);
    return this.axios.get(`${this.backendUrl}/team/${teamId}/races?${searchParams.toString()}`, {
      headers: {
        ...getAuthHeaders(),
        'Measurement-System': user.measurementSystem ?? 'imperial',
      },
    });
  }
}
